// Irrigation Reminder Service - Push notifications with sound and vibration
// Calculates irrigation timing based on crop, soil, and weather data

export interface IrrigationSchedule {
    id: string;
    fieldId: string;
    fieldName: string;
    cropType: string;
    scheduledTime: Date;
    duration: number; // minutes
    waterAmount: number; // liters
    frequency: 'daily' | 'alternate' | 'weekly' | 'custom';
    aiRecommendation?: string;
    isEnabled: boolean;
}

export interface IrrigationInput {
    fieldName: string;
    fieldArea: number; // in acres
    cropType: string;
    cropStage: 'seedling' | 'vegetative' | 'flowering' | 'fruiting' | 'maturity';
    soilType: 'clay' | 'loamy' | 'sandy' | 'black' | 'red';
    lastIrrigationDate: Date;
    soilMoisture?: number; // percentage from sensor
    weatherForecast?: {
        temperature: number;
        humidity: number;
        rainProbability: number;
    };
}

// Water requirement per acre per day (liters) by crop and stage
const CROP_WATER_REQUIREMENTS: Record<string, Record<string, number>> = {
    wheat: { seedling: 2000, vegetative: 3500, flowering: 4500, fruiting: 4000, maturity: 2500 },
    rice: { seedling: 5000, vegetative: 6500, flowering: 7500, fruiting: 7000, maturity: 5000 },
    cotton: { seedling: 2500, vegetative: 4000, flowering: 5000, fruiting: 4500, maturity: 3000 },
    soybean: { seedling: 2000, vegetative: 3000, flowering: 4000, fruiting: 3500, maturity: 2000 },
    maize: { seedling: 2500, vegetative: 4000, flowering: 5500, fruiting: 5000, maturity: 3500 },
};

// Soil water retention factor
const SOIL_RETENTION: Record<string, number> = {
    clay: 0.85,
    loamy: 1.0,
    sandy: 0.7,
    black: 0.9,
    red: 0.75,
};

// Irrigation interval in days by soil type
const IRRIGATION_INTERVALS: Record<string, number> = {
    clay: 4,
    loamy: 3,
    sandy: 2,
    black: 4,
    red: 2,
};

class IrrigationReminderService {
    private schedules: Map<string, IrrigationSchedule> = new Map();
    private reminderTimeouts: Map<string, NodeJS.Timeout> = new Map();
    private notificationSound: HTMLAudioElement | null = null;
    private isPermissionGranted = false;

    constructor() {
        this.initializeNotificationSound();
        this.requestNotificationPermission();
    }

    private initializeNotificationSound() {
        // Create notification sound (use built-in browser beep or custom audio)
        this.notificationSound = new Audio();
        this.notificationSound.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleWk7dZm+tI1tZ22RqKuMXlFgm8HKs41dOkR0prStjHBnYHmOrK+afV5bfZyxq5N0WV98mrGrl3ZiYIGZqaeTgGphgZWpqpmBcnKBjJ2mn4uBhI2TopmdhIaJkJahnIWCio2RnZ6HgYqLj5qchYOLi4+XmYWBiIqPl5WBgImKj5WUfn+HiY6TkXx+hYiMkI99f4SHi46NfH6Dhoq=';
    }

    async requestNotificationPermission(): Promise<boolean> {
        if (!('Notification' in window)) {
            console.warn('This browser does not support notifications');
            return false;
        }

        if (Notification.permission === 'granted') {
            this.isPermissionGranted = true;
            return true;
        }

        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            this.isPermissionGranted = permission === 'granted';
            return this.isPermissionGranted;
        }

        return false;
    }

    // Calculate next irrigation time based on inputs
    calculateNextIrrigation(input: IrrigationInput): {
        nextDate: Date;
        waterRequired: number;
        aiRecommendation: string;
        urgency: 'low' | 'medium' | 'high' | 'critical';
    } {
        const now = new Date();
        const lastIrrigation = new Date(input.lastIrrigationDate);
        const daysSinceLastIrrigation = Math.floor(
            (now.getTime() - lastIrrigation.getTime()) / (1000 * 60 * 60 * 24)
        );

        // Base interval from soil type
        let irrigationInterval = IRRIGATION_INTERVALS[input.soilType] || 3;

        // Adjust for crop stage
        if (input.cropStage === 'flowering' || input.cropStage === 'fruiting') {
            irrigationInterval = Math.max(1, irrigationInterval - 1);
        } else if (input.cropStage === 'maturity') {
            irrigationInterval += 1;
        }

        // Adjust for weather
        if (input.weatherForecast) {
            if (input.weatherForecast.rainProbability > 70) {
                irrigationInterval += 2; // Skip if rain expected
            } else if (input.weatherForecast.temperature > 38) {
                irrigationInterval = Math.max(1, irrigationInterval - 1); // More frequent in heat
            }
        }

        // Adjust for soil moisture
        if (input.soilMoisture !== undefined) {
            if (input.soilMoisture < 25) {
                irrigationInterval = 0; // Immediate irrigation needed
            } else if (input.soilMoisture > 60) {
                irrigationInterval += 1;
            }
        }

        // Calculate next irrigation date
        const nextDate = new Date(lastIrrigation);
        nextDate.setDate(nextDate.getDate() + irrigationInterval);

        // If next date is in the past, set to today
        if (nextDate < now) {
            nextDate.setTime(now.getTime());
            nextDate.setHours(6, 0, 0, 0); // 6 AM default
        }

        // Calculate water requirement
        const cropKey = input.cropType.toLowerCase();
        const baseWater = CROP_WATER_REQUIREMENTS[cropKey]?.[input.cropStage] || 3000;
        const retentionFactor = SOIL_RETENTION[input.soilType] || 1.0;
        const waterRequired = Math.round(baseWater * input.fieldArea / retentionFactor);

        // Determine urgency
        let urgency: 'low' | 'medium' | 'high' | 'critical' = 'low';
        const daysUntilNext = Math.ceil((nextDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (input.soilMoisture !== undefined && input.soilMoisture < 20) {
            urgency = 'critical';
        } else if (daysUntilNext <= 0) {
            urgency = 'high';
        } else if (daysUntilNext <= 1) {
            urgency = 'medium';
        }

        // Generate AI recommendation
        const aiRecommendation = this.generateAIRecommendation(input, waterRequired);

        return { nextDate, waterRequired, aiRecommendation, urgency };
    }

    private generateAIRecommendation(
        input: IrrigationInput,
        waterRequired: number
    ): string {
        const recommendations: string[] = [];

        // Weather-based recommendation
        if (input.weatherForecast) {
            if (input.weatherForecast.rainProbability > 70) {
                recommendations.push('🌧️ Rain expected - consider skipping irrigation to save ₹450');
            } else if (input.weatherForecast.temperature > 38) {
                recommendations.push('🌡️ High temperature - irrigate early morning (5-7 AM) to reduce evaporation');
            }
            if (input.weatherForecast.humidity < 40) {
                recommendations.push('💨 Low humidity - apply mulching to retain soil moisture');
            }
        }

        // Soil-based recommendation
        if (input.soilMoisture !== undefined) {
            if (input.soilMoisture < 25) {
                recommendations.push('⚠️ Critical: Soil moisture below 25% - immediate irrigation needed');
            } else if (input.soilMoisture > 70) {
                recommendations.push('✅ Adequate moisture - delay irrigation to prevent waterlogging');
            }
        }

        // Crop stage recommendation
        if (input.cropStage === 'flowering') {
            recommendations.push('🌸 Flowering stage - maintain consistent moisture for optimal yield');
        } else if (input.cropStage === 'seedling') {
            recommendations.push('🌱 Seedling stage - light, frequent irrigation recommended');
        }

        // Water amount
        recommendations.push(`💧 Recommended: ${waterRequired.toLocaleString()} liters for ${input.fieldArea} acres`);

        return recommendations.join('\n');
    }

    // Schedule a reminder notification
    scheduleReminder(schedule: IrrigationSchedule): void {
        this.schedules.set(schedule.id, schedule);

        // Clear existing timeout if any
        const existingTimeout = this.reminderTimeouts.get(schedule.id);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        const now = new Date();
        const reminderTime = new Date(schedule.scheduledTime);

        // Send reminder 15 minutes before scheduled time
        reminderTime.setMinutes(reminderTime.getMinutes() - 15);

        const delay = reminderTime.getTime() - now.getTime();

        if (delay > 0 && schedule.isEnabled) {
            const timeout = setTimeout(() => {
                this.triggerNotification(schedule);
            }, delay);

            this.reminderTimeouts.set(schedule.id, timeout);
            console.log(`Reminder scheduled for ${schedule.fieldName} at ${reminderTime.toLocaleString()}`);
        }
    }

    // Trigger notification with sound and vibration
    private async triggerNotification(schedule: IrrigationSchedule): Promise<void> {
        // Play sound
        this.playNotificationSound();

        // Trigger vibration (mobile devices)
        this.triggerVibration();

        // Show browser notification
        if (this.isPermissionGranted) {
            const notification = new Notification(`💧 Irrigation Reminder - ${schedule.fieldName}`, {
                body: `Scheduled in 15 minutes\n${schedule.aiRecommendation || 'Apply ' + schedule.waterAmount + ' liters'}`,
                icon: '/irrigation-icon.png',
                badge: '/badge-icon.png',
                tag: schedule.id,
                requireInteraction: true,
                vibrate: [200, 100, 200, 100, 200],
            } as NotificationOptions);

            notification.onclick = () => {
                window.focus();
                notification.close();
            };
        }

        // Dispatch custom event for in-app notification
        window.dispatchEvent(new CustomEvent('irrigationReminder', {
            detail: {
                schedule,
                timestamp: new Date(),
            }
        }));
    }

    private playNotificationSound(): void {
        try {
            if (this.notificationSound) {
                this.notificationSound.currentTime = 0;
                this.notificationSound.volume = 0.7;
                this.notificationSound.play().catch(e => console.warn('Audio play failed:', e));
            }
        } catch (error) {
            console.warn('Failed to play notification sound:', error);
        }
    }

    private triggerVibration(): void {
        if ('vibrate' in navigator) {
            // Vibration pattern: vibrate-pause-vibrate-pause-vibrate
            navigator.vibrate([200, 100, 200, 100, 400]);
        }
    }

    // Cancel a scheduled reminder
    cancelReminder(scheduleId: string): void {
        const timeout = this.reminderTimeouts.get(scheduleId);
        if (timeout) {
            clearTimeout(timeout);
            this.reminderTimeouts.delete(scheduleId);
        }
        this.schedules.delete(scheduleId);
    }

    // Get all active schedules
    getActiveSchedules(): IrrigationSchedule[] {
        return Array.from(this.schedules.values()).filter(s => s.isEnabled);
    }

    // Test notification (for demo)
    async testNotification(): Promise<void> {
        const testSchedule: IrrigationSchedule = {
            id: 'test-' + Date.now(),
            fieldId: 'test-field',
            fieldName: 'North Field',
            cropType: 'Wheat',
            scheduledTime: new Date(Date.now() + 15 * 60 * 1000),
            duration: 45,
            waterAmount: 2500,
            frequency: 'alternate',
            aiRecommendation: '🌡️ High temperature - irrigate early morning\n💧 Recommended: 2,500 liters',
            isEnabled: true,
        };

        await this.triggerNotification(testSchedule);
    }
}

// Singleton instance
export const irrigationReminderService = new IrrigationReminderService();
